import React, { Suspense, useCallback, useEffect, useState } from 'react';
import Sticky from 'react-stickynode';
import { useSelector } from 'react-redux';
import { trim, some } from 'lodash';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { useIntl } from 'react-intl';

import { WindowLocation } from '@reach/router';
import { useVariant } from '@unleash/proxy-client-react';
import { userPiDataSelector } from '../../../redux/selectors/cartSelectors';
import { phobioSelector } from '../../../redux/selectors/phoneSelectors';
import CompareToolBar from '../CompareToolBar/CompareToolBar';
import PreHeader from '../PreHeader/PreHeader';
import { ActiveStoreLocation } from '../../StoreLocator';
import TextLink from '../../UI/TextLink/TextLink';
import ShoppingCartHeaderIcon from '../../ReserveComponents/ShoppingCartHeaderIcon';
import SessionTimer from '../../SessionTimer/session-timer';
import extractArrayData from '../../../utils/extractArrayData';
import { Contentful } from '../../../types';

import BladeContainer from '../../UI/BladeContainer/BladeContainer';
import { ImageLoadingType } from '../../UI/Image/Image';
import { useFeatureFlag } from '../../FeatureFlag/useFeatureFlag';
import { FeatureFlags, VariantNames } from '../../FeatureFlag/featureFlags';
import {
  Container,
  InnerContainer,
  DesktopWrapper,
  MobileWrapper,
  LogoContainer,
  StyledImage,
  Navigation,
  NavLink,
  LanguagePickerWrapper,
  MainNav,
  HamburgerButton,
  StyledListItem,
  SSRHeaderContainer,
  SSRLogoContainer,
  SSRLanguagePicker,
} from './styles';
import LanguagePicker from './LanguagePicker';

const BurgerMenu = React.lazy(() => import('../../BurgerMenu/BurgerMenu'));

interface Props {
  headerData: Record<string, any>;
  location: WindowLocation<unknown>;
  preferredLocale?: string;
  selectLocaleToSave: () => void;
  preferredProvince?: string;
  selectProvinceToSave: () => void;
  skipToContentFocus: () => void;
}

const Header = ({
  headerData,
  location,
  preferredLocale,
  selectLocaleToSave,
  preferredProvince,
  selectProvinceToSave,
  skipToContentFocus,
}: Props) => {
  const { locale } = useIntl();
  const userPiData = useSelector(userPiDataSelector);
  const phobioDetails = useSelector(phobioSelector);
  // State for mobile nav display
  const [mobileBladeVisible, setMobileBladeVisible] = useState(false);
  const breakpoints = useBreakpoint();
  const [isClient, setIsClient] = useState(false);
  const [menuItemActive, setMenuItemActive] = useState('/');
  
  const variant = useVariant(FeatureFlags.NavigationActivePage);
  const showActiveState = variant.enabled && variant.name === VariantNames.WithActiveState;

  useEffect(() => {
    const gtmData = {
      event: 'navigation_active_page_experiment',
      featureFlag: FeatureFlags.NavigationActivePage,
      variant: variant.name,
    };
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window?.dataLayer?.push(gtmData);
    }
  }, [showActiveState, variant]);

  // Set Menu Item Active based on page URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const getCurrentPage = window.location.pathname;
      const getPageUrl = getCurrentPage
        .replace(locale, '')
        .replace(/\//gm, '');
  
      if (getPageUrl === '') {
        setMenuItemActive('/');
      } else {
        setMenuItemActive(`/${getPageUrl}`);
      }
    }
  }, [locale, menuItemActive]);

  const isReserveAccessoriesFFEnabled = useFeatureFlag(FeatureFlags.ReserveAccessories);

  useEffect(() => {
    setIsClient(true);
  }, []);
  const isMobileClient = isClient && breakpoints.md;
  const isDesktopClient = isClient && !breakpoints.md && breakpoints.desktop;

  const [isStuck, setIsStuck] = useState(false);

  const handleStickyChange = ({ status }) => setIsStuck(status === Sticky.STATUS_FIXED);

  const sessionTimerData = extractArrayData(
    headerData.headerBlades,
    Contentful.ContentfulId,
    '5bnK3Tw6PzWqAd6ZCR8EJ8',
  ).bladeSections;

  const timerBladeWait = extractArrayData(
    sessionTimerData,
    Contentful.ContentfulId,
    '66f6yspjNRbDUGLvah6sP9',
  ).value;
  const totalIdleTime = extractArrayData(
    sessionTimerData,
    Contentful.ContentfulId,
    '1qoojWoJqljUNlBrWErzfM',
  ).value;

  const Language = useCallback(() => (
    <LanguagePicker
      preferredProvince={preferredProvince}
      skipToContentFocus={skipToContentFocus}
      preferredLocale={preferredLocale}
      selectLocaleToSave={selectLocaleToSave}
      selectProvinceToSave={selectProvinceToSave}
    />
  ), [
    preferredLocale,
    preferredProvince,
    selectLocaleToSave,
    selectProvinceToSave,
    skipToContentFocus,
  ]);

  return (
    <Sticky
      enabled
      innerZ={999}
      enableTransforms={false}
      onStateChange={handleStickyChange}
    >
      {(some(userPiData, (item) => trim(item) !== '') || phobioDetails) && (
        <SessionTimer
          cms={sessionTimerData}
          timeout={totalIdleTime * 60 * 1000}
          promptBeforeIdle={timerBladeWait * 60 * 1000}
        />
      )}
      <Container className={isStuck && 'is-stuck'}>
        {/* PREHEADER */}
        {headerData.announcementBanner?.map((banner) => (
          <PreHeader
            bannerData={banner}
            preferredProvince={preferredProvince}
            location={location}
            key={banner.contentful_id}
          />
        ))}

        {/* MOBILE MENU */}

        {isMobileClient && (
          <MobileWrapper>
            <LogoContainer locale={preferredLocale}>
              <NavLink to={`/${preferredLocale}`} tabIndex="0">
                <StyledImage image={headerData.logoDam} />
              </NavLink>
            </LogoContainer>

            {isReserveAccessoriesFFEnabled && (
            <>
              <ActiveStoreLocation className="mobile-menu" />
              <ShoppingCartHeaderIcon className="mobile-menu" />
            </>
            )}

            {/* Burger button */}
            <HamburgerButton
              type="button"
              onClick={() => {
                setMobileBladeVisible(!mobileBladeVisible);
              }}
              aria-expanded={mobileBladeVisible}
              aria-label="Hamburger menu button"
            >
              <div className="hamburgerBar" />
              <div className="hamburgerBar" />
              <div className="hamburgerBar" />
            </HamburgerButton>

            {/* This is the mobile nav menu */}
            <BladeContainer
              locale={preferredLocale}
              bladeState={mobileBladeVisible}
              setBladeState={setMobileBladeVisible}
              bladeWidth={1023}
              logo={headerData.logoDam}
            >
              <Suspense>
                <BurgerMenu
                  menuLinks={headerData.headerLinks}
                  menuItemActive={menuItemActive}
                  count={headerData.headerLinks.length}
                  preferredLocale={preferredLocale}
                  selectLocaleToSave={selectLocaleToSave}
                  preferredProvince={preferredProvince}
                  selectProvinceToSave={selectProvinceToSave}
                  onClosestStoreClick={() => {
                    setMobileBladeVisible(false);
                  }}
                  setMobileBladeVisible={setMobileBladeVisible}
                />
              </Suspense>
            </BladeContainer>
          </MobileWrapper>
        )}

        {/* DESKTOP HEADER */}
        {isDesktopClient && (
          <DesktopWrapper>
            {/* LANGUAGE AND PROVINCE */}
            <LanguagePickerWrapper>
              <Language />
            </LanguagePickerWrapper>

            {/* LOGO */}
            <InnerContainer
              className="navigation"
              role="navigation"
              aria-label="navigation"
            >
              <LogoContainer locale={preferredLocale}>
                <NavLink to={`/${preferredLocale}`}>
                  <StyledImage image={headerData.logoDam} loading={ImageLoadingType.Eager} />
                </NavLink>
              </LogoContainer>

              <MainNav
                locale={preferredLocale}
                count={headerData.headerLinks.length}
              >
                {/* NAV ITEMS */}
                <Navigation
                  aria-label="primary"
                  locale={preferredLocale}
                  count={headerData.headerLinks.length}
                >
                  <ul>
                    {headerData.headerLinks.map(
                      (link) => !(
                        preferredLocale === 'fr' && link.ctaUrl === '/blog'
                      ) && (
                      <StyledListItem
                        key={link.contentful_id}
                        className={link.ctaUrl.replace('/', '')}
                        color={link.ctaColor}
                      >
                        <TextLink
                          linkText={link.ctaLabel.ctaLabel}
                          url={link.ctaUrl}
                          route={link.ctaTarget}
                          locale={preferredLocale}
                          className={
                            menuItemActive === link.ctaUrl
                              ? 'active'
                              : ''
                          }
                          showActiveState={showActiveState}
                        />
                      </StyledListItem>
                      ),
                    )}
                  </ul>

                  <ActiveStoreLocation />
                  {isReserveAccessoriesFFEnabled && (
                    <ShoppingCartHeaderIcon />
                  )}
                </Navigation>
              </MainNav>
            </InnerContainer>
          </DesktopWrapper>
        )}

        {!isClient && (
          <SSRHeaderContainer>
            <SSRLanguagePicker>
              <Language />
            </SSRLanguagePicker>
            <SSRLogoContainer>
              <LogoContainer locale={preferredLocale}>
                <NavLink to={`/${preferredLocale}`} tabIndex="0">
                  <StyledImage image={headerData.logoDam} />
                </NavLink>
              </LogoContainer>
            </SSRLogoContainer>
          </SSRHeaderContainer>
        )}
        <CompareToolBar location={location} />
      </Container>
    </Sticky>
  );
};

export default Header;
